import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { initScripts } from '@o2/ui';
import o2UiPackageJson from '@o2/ui/package.json';

import { environment } from 'environments/environment';
import { FinancialDocumentDetail } from 'generated/bffFinancialInfo/models';
import { AppAction, createActionCreator, createActionType, RequestActionTypes } from 'helpers/redux/reduxHelpers';
import { getCurrentRoute } from 'helpers/routes/routes';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { ExternalUser } from 'models/context/ExternalUser';
import { InternalUser } from 'models/context/InternalUser';
import { LocaleTypeMap, UserPreferences } from 'models/context/UserPreferences';
import { KahlMessage } from 'models/KahlMessage';
import { selectBillingDetailFinancialDocumentDetail } from 'saga/billing/detail/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { selectContextExternalUser } from 'saga/context/external/ducks';
import { selectContextInternalUser } from 'saga/context/internal/ducks';
import { selectContextUserPreferencesInfo } from 'saga/context/userPreferences/ducks';
import { KahlActionTypes } from 'saga/kahl/ducks';
import { selectProperty } from 'saga/property/ducks';

export const IS_ADOBE_ANALYTICS_ENABLED_PROPERTY = 'onecrm.general.adobeAnalyticsEnabled';

/* STATE */

/* ACTION TYPES */
export enum AdobeAnalyticsActionTypes {
  PushAdobeAnalyticsEvent = '@@AdobeAnalytics/PUSH_EVENT',
  PushAdobeAnalyticsDataOrAttr = '@@AdobeAnalytics/PUSH_DATA_OR_ATTR',
  PushAdobeAnalyticsEventAndLoad = '@@AdobeAnalytics/PUSH_EVENT_AND_LOAD',
  PushAdobeAnalyticsEventFormSubmit = '@@AdobeAnalytics/PUSH_FORM_SUBMIT',
  PushAdobeAnalyticsInvoiceDetail = '@@AdobeAnalytics/PUSH_INVOICE_DETAIL',
  AdobeAnalyticsAfterPageLoadHandler = '@@AdobeAnalytics/AFTER_PAGE_LOAD_HANDLER',
}

/* ACTIONS */
export const pushAdobeAnalyticsEventAction = createActionCreator(AdobeAnalyticsActionTypes.PushAdobeAnalyticsEvent);
export const pushAdobeAnalyticsDataOrAttrAction = createActionCreator(
  AdobeAnalyticsActionTypes.PushAdobeAnalyticsDataOrAttr
);
export const pushAdobeAnalyticsEventAndLoadAction = createActionCreator(
  AdobeAnalyticsActionTypes.PushAdobeAnalyticsEventAndLoad
);
export const pushAdobeAnalyticsEventFormSubmitAction = createActionCreator(
  AdobeAnalyticsActionTypes.PushAdobeAnalyticsEventFormSubmit
);
export const pushAdobeAnalyticsInvoiceDetailAction = createActionCreator(
  AdobeAnalyticsActionTypes.PushAdobeAnalyticsInvoiceDetail
);
export const adobeAnalyticsAfterPageLoadHandlerAction = createActionCreator(
  AdobeAnalyticsActionTypes.AdobeAnalyticsAfterPageLoadHandler
);

/* REDUCERS */

/* SELECTORS */
/**
 * Gets value of global property adobeDataLayer.
 * Empty array is initialized if no adobeDataLayer exists.
 */
export const selectAdobeAnalyticsData = (): any[] => {
  if (!isAdobeAnalyticsEnabled()) {
    window.adobeDataLayer = [];

    return [];
  }

  initScripts(environment.buildProperties.adobeLaunchPath);

  return window.adobeDataLayer;
};

/**
 * Checks whether Adobe Analytics is enabled/disabled.
 */
export const isAdobeAnalyticsEnabled = (): boolean => {
  const adobeAnalyticsEnabledValue: string = selectProperty(IS_ADOBE_ANALYTICS_ENABLED_PROPERTY);

  return adobeAnalyticsEnabledValue?.toLowerCase() === 'true';
};

/* SAGAS */
/**
 * Pushes the event (eventType + eventData) object to the adobeDataLayer
 * and then pushes the 'pageLoaded' event.
 * @param eventType event type
 * @param eventData any event data
 * @see isAdobeAnalyticsEnabled
 * @see pushAdobeAnalyticsEvent
 */
function* pushAdobeAnalyticsEventAndLoad({ payload: { eventType, eventData } }: any) {
  const adobeAnalyticsEnabled: boolean = yield select(isAdobeAnalyticsEnabled);

  if (!adobeAnalyticsEnabled) {
    console.debug('AdobeAnalytics is disabled. Skip action: "pushAdobeAnalyticsEventAndLoad".');

    return;
  }

  yield put(
    pushAdobeAnalyticsEventAction({
      eventType: eventType,
      eventData: eventData,
    })
  );
  yield put(pushAdobeAnalyticsEventAction({ eventType: 'pageLoaded' }));
}

function* pushAdobeAnalyticsEventFormSubmit({ payload: formId }: AppAction<string>) {
  const adobeAnalyticsEnabled: boolean = yield select(isAdobeAnalyticsEnabled);

  if (!adobeAnalyticsEnabled) {
    console.debug('AdobeAnalytics is disabled. Skip action: "pushAdobeAnalyticsEventFormSubmit".');

    return;
  }

  yield put(
    pushAdobeAnalyticsEventAndLoadAction({
      eventType: 'formSubmit',
      eventData: { formId: formId },
    })
  );
}

/**
 * Pushes the event (eventType + eventData) object to the adobeDataLayer.
 * @param eventType event type
 * @param eventData any event data
 * @see isAdobeAnalyticsEnabled
 * @see pushAdobeAnalyticsDataOrAttr
 */
function* pushAdobeAnalyticsEvent({ payload: { eventType, eventData } }: any) {
  const adobeAnalyticsEnabled: boolean = yield select(isAdobeAnalyticsEnabled);

  if (!adobeAnalyticsEnabled) {
    console.debug('AdobeAnalytics is disabled. Skip action: "pushAdobeAnalyticsEvent".');

    return;
  }

  yield put(
    pushAdobeAnalyticsDataOrAttrAction({
      event: eventType,
      eventData: eventData,
    })
  );
}

/**
 * Pushes any object or key-value attributes to the adobeDataLayer.
 * 'null' and 'undefined' values are removed from the payload.
 * @param payload any object or key-value map
 * @see isAdobeAnalyticsEnabled
 * @see selectAdobeAnalyticsData
 */
function* pushAdobeAnalyticsDataOrAttr({ payload }: any) {
  const adobeAnalyticsEnabled: boolean = yield select(isAdobeAnalyticsEnabled);

  if (!adobeAnalyticsEnabled) {
    console.debug('AdobeAnalytics is disabled. Skip action: "pushAdobeAnalyticsDataOrAttr"');

    return;
  }

  // filter null and undefined attributes
  const objectToPush = JSON.parse(JSON.stringify(payload, (k, v) => (v === null ? undefined : v)));
  // push to adobeDataLayer
  const adobeDataLayer: any[] = yield select(selectAdobeAnalyticsData);

  adobeDataLayer.push(objectToPush);
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Components.GUIMessage.display GUI message.pushAdobeAnalyticsEventAndLoad
 */
function* pushPopupMessage({ payload }: AppAction<KahlMessage>) {
  yield put(
    pushAdobeAnalyticsEventAction({
      eventType: 'popupMessage',
      eventData: {
        messageType: payload?.errorType === '2' ? 'success' : 'error',
        messageId: payload?.kahlId || 'ID0013',
        messageDescription: payload?.text,
      },
    })
  );
}

function* pushInvoiceDetail({ payload: pushPageLoaded }: AppAction<boolean>) {
  const financialDocument: FinancialDocumentDetail = yield select(selectBillingDetailFinancialDocumentDetail);

  if (getCurrentRoute()?.commonPageData.pageId === '2.2.1.1' && financialDocument) {
    yield put(
      pushAdobeAnalyticsDataOrAttrAction({
        invoiceDetail: {
          invoiceNo: financialDocument.invoiceNumExt || financialDocument.invoiceNumInt,
        },
      })
    );

    if (pushPageLoaded) {
      yield put(pushAdobeAnalyticsEventAction({ eventType: 'pageLoaded' }));
    }
  }
}

/**
 * EA: Projects.O2 Czech Republic.MO2-CARE.Processes.Common.Components.AdobeAnalytics.adobeAnalyticsAfterPageLoadHandler.adobeAnalyticsAfterPageLoadHandler
 */
function* afterPageLoadHandler() {
  const adobeAnalyticsEnabled: boolean = yield select(isAdobeAnalyticsEnabled);

  if (!adobeAnalyticsEnabled) {
    console.debug('AdobeAnalytics is disabled. Skip action: "afterPageLoadHandler"');

    return;
  }

  // Push common attributes to adobeDataLayer
  const userPreferences: UserPreferences = yield select(selectContextUserPreferencesInfo);
  const pageData = getCurrentRoute()?.commonPageData;

  yield put(
    pushAdobeAnalyticsDataOrAttrAction({
      pageId: pageData?.pageId || null,
      pageTitle: document.title,
      language: (userPreferences?.locale || LocaleTypeMap[environment.appTranslation.defaultLocale]).replace('_', '-'),
      themeVersion: o2UiPackageJson.version,
    })
  );

  // Push external user and customer data
  const externalUser: ExternalUser = yield select(selectContextExternalUser);
  const customer: CustomerInfo = yield select(selectContextCustomerInfo);
  let userRoles;

  if (customer && externalUser) {
    userRoles = externalUser?.entityAccesses
      ?.filter((ea) => ea.cuRefNo === customer.cuRefNo)
      .filter((ea) => ea.isTrusted)
      .map((ea) => ea.roleType);
  }

  yield put(
    pushAdobeAnalyticsDataOrAttrAction({
      user: {
        userType: 'external',
        basicServiceType: 'postpaid',
        customerRefNo: customer?.cuRefNo,
        customerRefSegment: customer?.segment,
        userName: externalUser?.login,
        usernameE110: customer?.primaryAdmin?.email,
        externalUserRole: userRoles,
        msisdnE110: customer?.primaryAdmin?.phoneNoMobile,
        msisdn: externalUser?.phoneNoMobile,
        userFirstName: externalUser?.firstName,
        userLastName: externalUser?.lastName,
        customerName: customer?.displayName,
        customerCareLevel: customer?.customerCareLevel,
        customerSubjectType: customer?.subjectType,
        customerMarketingAgreement: customer?.marketingAgreement,
      },
    })
  );

  // push Internal user data
  const internalUser: InternalUser = yield select(selectContextInternalUser);

  if (internalUser) {
    yield put(
      pushAdobeAnalyticsDataOrAttrAction({
        userInternal: {
          userType: 'internal',
          userName: internalUser.login,
        },
      })
    );
  }

  yield put(pushAdobeAnalyticsInvoiceDetailAction());

  // Push pageLoad event
  yield put(pushAdobeAnalyticsEventAction({ eventType: 'pageLoaded' }));
}

/* EXPORT */
export function* adobeAnalyticsSaga() {
  yield takeLatest(
    createActionType(AdobeAnalyticsActionTypes.AdobeAnalyticsAfterPageLoadHandler),
    afterPageLoadHandler
  );

  yield takeEvery(
    createActionType(AdobeAnalyticsActionTypes.PushAdobeAnalyticsDataOrAttr),
    pushAdobeAnalyticsDataOrAttr
  );

  yield takeEvery(
    createActionType(AdobeAnalyticsActionTypes.PushAdobeAnalyticsEventAndLoad),
    pushAdobeAnalyticsEventAndLoad
  );

  yield takeEvery(
    createActionType(AdobeAnalyticsActionTypes.PushAdobeAnalyticsEventFormSubmit),
    pushAdobeAnalyticsEventFormSubmit
  );

  yield takeLatest(createActionType(AdobeAnalyticsActionTypes.PushAdobeAnalyticsInvoiceDetail), pushInvoiceDetail);

  yield takeEvery(createActionType(AdobeAnalyticsActionTypes.PushAdobeAnalyticsEvent), pushAdobeAnalyticsEvent);

  /*
   * Saga action registration related to Adobe Analytics matching GetKahl actions
   * 'ReferenceError' was caused when 'pushAdobeAnalyticsEventAndLoadAction' is called from kahl/ducks.
   */
  yield takeEvery(
    createActionType(createActionType(KahlActionTypes.GetKahl, RequestActionTypes.SUCCESS)),
    pushPopupMessage
  );
  yield takeEvery(
    createActionType(createActionType(KahlActionTypes.GetKahl, RequestActionTypes.FAILURE)),
    pushPopupMessage
  );
}
